import posthog from 'posthog-js';
import { isBasicAnalyticsCaptureAllowed } from './consent';

type AppPageGroup = 'auth' | 'dashboard' | 'designs' | 'batches' | 'billing' | 'tasks';
type AppPageType = 'auth' | 'home' | 'list' | 'detail' | 'flow' | 'billing';

export interface AppPageContext {
  surface: 'app';
  page_group: AppPageGroup;
  page_type: AppPageType;
  page_slug: string;
}

const ATTR_MAP: [string, string, boolean][] = [
  ['data-ph-component', 'component_id', false],
  ['data-ph-element', 'element_id', false],
  ['data-ph-dest-type', 'destination_type', false],
  ['data-ph-dest', 'destination_path', false],
  ['data-ph-variant', 'variant', false],
  ['data-ph-plan', 'plan_id', false],
  ['data-ph-section', 'section_id', false],
  ['data-ph-content-type', 'content_type', false],
  ['data-ph-content-slug', 'content_slug', false],
  ['data-ph-position', 'position_index', true],
  ['data-ph-entity-type', 'entity_type', false],
  ['data-ph-entity-id', 'entity_id', false],
  ['data-ph-task-type', 'task_type', false],
  ['data-ph-filter', 'filter_name', false],
  ['data-ph-value', 'value', false],
];

let hasInitializedIntentTracking = false;

function normalizePath(pathname: string): string {
  return pathname.replace(/\/$/, '') || '/';
}

export function getAppPageContext(pathname: string = window.location.pathname): AppPageContext {
  const path = normalizePath(pathname);

  if (path.startsWith('/sign-in')) {
    return { surface: 'app', page_group: 'auth', page_type: 'auth', page_slug: 'sign_in' };
  }

  if (path.startsWith('/sign-up')) {
    return { surface: 'app', page_group: 'auth', page_type: 'auth', page_slug: 'sign_up' };
  }

  if (path === '/') {
    return { surface: 'app', page_group: 'dashboard', page_type: 'home', page_slug: 'dashboard' };
  }

  if (path === '/designs') {
    return { surface: 'app', page_group: 'designs', page_type: 'list', page_slug: 'designs' };
  }

  if (path === '/designs/upload') {
    return { surface: 'app', page_group: 'designs', page_type: 'flow', page_slug: 'design_upload' };
  }

  if (path.startsWith('/designs/')) {
    return { surface: 'app', page_group: 'designs', page_type: 'detail', page_slug: 'design_detail' };
  }

  if (path === '/batches') {
    return { surface: 'app', page_group: 'batches', page_type: 'list', page_slug: 'batches' };
  }

  if (path.startsWith('/batches/')) {
    return { surface: 'app', page_group: 'batches', page_type: 'detail', page_slug: 'batch_detail' };
  }

  if (path === '/billing') {
    return { surface: 'app', page_group: 'billing', page_type: 'billing', page_slug: 'billing' };
  }

  if (path === '/tasks') {
    return { surface: 'app', page_group: 'tasks', page_type: 'list', page_slug: 'tasks' };
  }

  return { surface: 'app', page_group: 'dashboard', page_type: 'home', page_slug: 'dashboard' };
}

export function registerAppPageContext(pathname: string = window.location.pathname): AppPageContext {
  const ctx = getAppPageContext(pathname);
  posthog.register(ctx);
  return ctx;
}

function readDataProps(el: HTMLElement): Record<string, unknown> {
  const props: Record<string, unknown> = {};

  for (const [attr, prop, isInt] of ATTR_MAP) {
    const value = el.getAttribute(attr);
    if (value == null) continue;
    props[prop] = isInt ? parseInt(value, 10) : value;
  }

  return props;
}

export function initAppIntentAnalytics(): void {
  if (hasInitializedIntentTracking) return;
  hasInitializedIntentTracking = true;

  document.addEventListener('click', (event) => {
    if (!isBasicAnalyticsCaptureAllowed()) return;

    const target = event.target as HTMLElement | null;
    const el = target?.closest<HTMLElement>('[data-ph-event]');
    if (!el) return;

    const eventName = el.getAttribute('data-ph-event');
    if (!eventName) return;

    posthog.capture(eventName, readDataProps(el));
  });
}
